import {
  IDriverRepository,
  IDriverQuery,
  IDriverAggregatesQuery,
} from '@/core/domain/repositories/IDriverRepository'
import {
  Driver,
  DriverAggregates,
  OnlineTimeData,
} from '@/core/domain/entities/Driver'
import { Result, createOk, createErr } from '@/core/shared/utils/Result'
import { UserId, DriverId } from '@/core/shared/types'
import { GraphQLClientInterface } from '@/infrastructure/graphql/apolloClient'
import {
  GET_DRIVERS,
  GET_DRIVER,
  GET_DRIVER_AGGREGATES,
  GET_ONLINE_TIME,
} from '@/infrastructure/graphql/queries/driverQueries'

export class GraphQLDriverRepository implements IDriverRepository {
  constructor(private readonly graphqlClient: GraphQLClientInterface) {}

  async getDrivers(userId: UserId): Promise<Result<Driver[], Error>> {
    try {
      const data = await this.graphqlClient.query<
        { drivers?: Driver[] },
        IDriverQuery
      >({
        query: GET_DRIVERS,
        variables: { userId },
      })
      if (!data) {
        return createErr(new Error('No data returned from drivers query'))
      }
      const drivers = data.drivers ?? []
      return createOk<Driver[]>(drivers)
    } catch (error) {
      console.error('Failed to fetch drivers:', error)
      return createErr(
        error instanceof Error ? error : new Error('Unknown error occurred')
      )
    }
  }

  async getDriver(
    userId: UserId,
    driverId: DriverId
  ): Promise<Result<Driver | null, Error>> {
    try {
      const data = await this.graphqlClient.query<
        { driver: Driver | null },
        { userId: UserId; id: DriverId }
      >({
        query: GET_DRIVER,
        variables: { userId, id: driverId },
      })

      if (!data) {
        return createErr(new Error('No data returned from driver query'))
      }
      return createOk(data.driver)
    } catch (error) {
      console.error('Failed to fetch driver:', error)
      return createErr(
        error instanceof Error ? error : new Error('Unknown error occurred')
      )
    }
  }

  async getDriverPerformance(
    userId: UserId,
    driverId: DriverId,
    period: string
  ): Promise<Result<Driver, Error>> {
    try {
      const data = await this.graphqlClient.query<
        { driver: Driver | null },
        { userId: UserId; id: DriverId; period: string }
      >({
        query: GET_DRIVER,
        variables: { userId, id: driverId, period },
      })

      if (!data) {
        console.warn('⚠️ [GraphQLDriverRepository] No data returned for performance')
        return createErr(new Error('No data returned for performance'))
      }

      console.log('📦 [GraphQLDriverRepository] Performance data received:', {
        hasDriver: !!data.driver,
        driver: data.driver,
      })

      if (!data.driver) {
        console.warn(
          '⚠️ [GraphQLDriverRepository] Driver not found for performance'
        )
        return createErr(new Error('Driver not found'))
      }

      return createOk(data.driver)
    } catch (error) {
      console.error(
        '❌ [GraphQLDriverRepository] Failed to fetch driver performance:',
        error
      )
      return createErr(
        error instanceof Error ? error : new Error('Unknown error occurred')
      )
    }
  }

  async getDriverAggregates(
    userId: UserId
  ): Promise<Result<DriverAggregates, Error>> {
    try {
      const data = await this.graphqlClient.query<
        { driverAggregates: DriverAggregates },
        IDriverAggregatesQuery
      >({
        query: GET_DRIVER_AGGREGATES,
        variables: { userId },
      })

      if (!data) {
        return createErr(
          new Error('No data returned from driver aggregates query')
        )
      }
      return createOk(data.driverAggregates)
    } catch (error) {
      console.error('Failed to fetch driver aggregates:', error)
      return createErr(
        error instanceof Error ? error : new Error('Unknown error occurred')
      )
    }
  }

  async getDriverOnlineTime(
    driverId: DriverId,
    dateStart: string,
    dateEnd: string
  ): Promise<Result<OnlineTimeData, Error>> {
    try {
      const formattedDateStart = dateStart.split('T')[0]
      const formattedDateEnd = dateEnd.split('T')[0]

      console.log(
        '⏱️ [GraphQLDriverRepository] Fetching online time via GraphQL:',
        {
          driverId,
          dateStart: formattedDateStart,
          dateEnd: formattedDateEnd,
        }
      )

      const data = await this.graphqlClient.query<
        { onlineTime: OnlineTimeData | null },
        {
          driverId: DriverId
          dateStart: string
          dateEnd: string
        }
      >({
        query: GET_ONLINE_TIME,
        variables: {
          driverId,
          dateStart: formattedDateStart,
          dateEnd: formattedDateEnd,
        },
      })

      if (!data) {
        console.warn('⚠️ [GraphQLDriverRepository] No data returned for online time')
        return createErr(new Error('No data returned for online time'))
      }

      if (!data.onlineTime) {
        console.warn('⚠️ [GraphQLDriverRepository] Online time not found')
        return createErr(new Error('Online time data not found'))
      }

      console.log('✅ [GraphQLDriverRepository] Online time received:', {
        driverId,
        dateStart: formattedDateStart,
        dateEnd: formattedDateEnd,
        onlineTimeData: data.onlineTime,
      })

      return createOk(data.onlineTime)
    } catch (error) {
      console.error(
        '❌ [GraphQLDriverRepository] Failed to fetch driver online time:',
        error
      )
      return createErr(
        error instanceof Error
          ? error
          : new Error('Failed to fetch online time')
      )
    }
  }
}
